import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarDay,
  FaCalendarAlt,
  FaListAlt,
  FaUsers,
  FaDollarSign,
  FaTicketAlt,
  FaSync,
  FaStickyNote,
  FaPlus,
} from "react-icons/fa";
import axios from "axios";
import { useAuth } from "../context/authContext";
import {
  getSriLankaToday,
  getSriLankaDaysAgo,
  formatSriLankaDate,
} from "../utils/dateUtils";
import "./dashboard.css";
import TeamFloatingNavigation from "../components/TeamFloatingNavigation";

export default function TeamDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState({
    today: { tickets: 0, total: 0, sales: 0 },
    last7: { tickets: 0, total: 0, sales: 0 },
    last30: { tickets: 0, total: 0, sales: 0 },
    all: { tickets: 0, total: 0, sales: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [recentSales, setRecentSales] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-sales`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = res.data;

        // Use Sri Lankan timezone for date calculations
        const todayStr = getSriLankaToday();
        const last7Str = getSriLankaDaysAgo(6);
        const last30Str = getSriLankaDaysAgo(29);

        let todayStats = { tickets: 0, total: 0, sales: 0 };
        let weekStats = { tickets: 0, total: 0, sales: 0 };
        let monthStats = { tickets: 0, total: 0, sales: 0 };
        let allStats = { tickets: 0, total: 0, sales: 0 };

        data.forEach((sale) => {
          const date = sale.date;
          allStats.tickets += sale.numberOfTickets;
          allStats.total += sale.totalPrice;
          allStats.sales += 1;

          if (date === todayStr) {
            todayStats.tickets += sale.numberOfTickets;
            todayStats.total += sale.totalPrice;
            todayStats.sales += 1;
          }
          if (date >= last7Str) {
            weekStats.tickets += sale.numberOfTickets;
            weekStats.total += sale.totalPrice;
            weekStats.sales += 1;
          }
          if (date >= last30Str) {
            monthStats.tickets += sale.numberOfTickets;
            monthStats.total += sale.totalPrice;
            monthStats.sales += 1;
          }
        });

        setStats({
          today: todayStats,
          last7: weekStats,
          last30: monthStats,
          all: allStats,
        });

        // Get recent 5 sales for quick overview
        const sortedSales = data
          .sort(
            (a, b) =>
              new Date(`${b.date} ${b.time}`) - new Date(`${a.date} ${a.time}`)
          )
          .slice(0, 5);
        setRecentSales(sortedSales);
      } catch (err) {
        console.error("Failed to fetch sales data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const buttons = [
    {
      label: "Sales Overview",
      path: "/viewSale",
      icon: <FaListAlt />,
      description: "View all sales data",
    },
    {
      label: "Add Sale",
      path: "/addSale",
      icon: <FaTicketAlt />,
      description: "Record new sale",
    },
    {
      label: "Add Note",
      path: "/add-note",
      icon: <FaStickyNote />,
      description: "Add team notes",
    },
    {
      label: "Add Guide",
      path: "/add-guide",
      icon: <FaUsers />,
      description: "Register new guide",
    },
  ];

  const statCards = [
    {
      title: "Total Revenue",
      value: `LKR ${stats.all.total.toLocaleString()}`,
      subtitle: `${stats.all.sales} sales`,
      icon: <FaDollarSign />,
      color: "revenue",
    },
    {
      title: "Total Tickets",
      value: stats.all.tickets.toLocaleString(),
      subtitle: "All time",
      icon: <FaTicketAlt />,
      color: "tickets",
    },
    {
      title: "Last 30 Days",
      value: `LKR ${stats.last30.total.toLocaleString()}`,
      subtitle: `${stats.last30.tickets} tickets`,
      icon: <FaCalendarAlt />,
      color: "month",
    },
    {
      title: "Today",
      value: `${stats.today.tickets} tickets`,
      subtitle: `LKR ${stats.today.total.toLocaleString()}`,
      icon: <FaCalendarDay />,
      color: "today",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans text-slate-700">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-base text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-700">
      {/* Header Section */}
      <div className="bg-white border-b border-slate-200 py-8">
        <div className="w-full max-w-[1600px] mx-auto px-6 flex justify-between items-center flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white">
              <FaListAlt className="text-lg" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800">Team Dashboard</h1>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-300 transition-colors duration-200 text-sm font-medium"
          >
            <FaSync className="text-xs" />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {statCards.map((card, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-shadow duration-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">{card.title}</p>
                  <p className="text-2xl font-bold text-slate-800 mt-2">{card.value}</p>
                  <p className="text-sm text-slate-500 mt-1">{card.subtitle}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white ${
                  card.color === 'revenue' ? 'bg-emerald-500' :
                  card.color === 'tickets' ? 'bg-blue-500' :
                  card.color === 'month' ? 'bg-violet-500' :
                  'bg-amber-500'
                }`}>
                  {card.icon}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Quick Actions Section */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6 sm:mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <FaPlus className="text-blue-500" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {buttons.map((btn, idx) => (
              <button
                key={idx}
                onClick={() => navigate(btn.path)}
                className="group bg-white border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50 rounded-xl p-6 text-center transition-all duration-200 hover:shadow-md"
                title={btn.description}
              >
                <div className="w-12 h-12 bg-slate-100 group-hover:bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4 transition-colors duration-200">
                  <div className="text-slate-600 group-hover:text-white text-lg transition-colors duration-200">
                    {btn.icon}
                  </div>
                </div>
                <h3 className="font-semibold text-slate-800 group-hover:text-blue-700 mb-2 transition-colors duration-200">
                  {btn.label}
                </h3>
                <p className="text-sm text-slate-500 group-hover:text-slate-600 transition-colors duration-200">
                  {btn.description}
                </p>
              </button>
            ))}
          </div>
        </div>

        
        {/* Recent Activity Section */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6 sm:mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <FaListAlt className="text-blue-500" />
            Recent Sales Activity
          </h2>
          
          {recentSales.length === 0 ? (
            <div className="text-center py-12 px-6 text-slate-500">
              <FaTicketAlt className="text-4xl mx-auto mb-4 text-slate-300" />
              <p className="text-base mb-4">No recent sales found</p>
              <button
                onClick={() => navigate("/addSale")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200"
              >
                <FaPlus className="text-sm" />
                Add First Sale
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg text-sm font-medium text-slate-600 uppercase tracking-wide border-b-2 border-slate-200">
                <span>Customer</span>
                <span>Tickets</span>
                <span>Amount</span>
                <span>Date</span>
              </div>
              
              <div className="divide-y divide-slate-200">
                {recentSales.map((sale, index) => (
                  <div key={index} className="grid grid-cols-4 gap-4 p-4 hover:bg-slate-50 transition-colors duration-150">
                    <div className="flex flex-col gap-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 w-fit">
                        {sale.customerType}
                      </span>
                      <span className="text-sm text-slate-600">{sale.bookingType}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {sale.numberOfTickets} tickets
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-semibold text-slate-800">
                        LKR {sale.totalPrice.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-slate-800">
                        {formatSriLankaDate(sale.date)}
                      </span>
                      <span className="text-xs text-slate-500">{sale.time}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate("/viewSale")}
                  className="inline-flex items-center px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-300 transition-colors duration-200 font-medium"
                >
                  View All Sales
                </button>
              </div>
            </div>
          )}
        </div>

        
        {/* Performance Summary Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Weekly Performance */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <FaCalendarAlt className="text-blue-500" />
              Weekly Performance
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">This Week</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {stats.last7.tickets} tickets
                  </p>
                  <p className="text-sm text-slate-500">
                    LKR {stats.last7.total.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <FaTicketAlt className="text-emerald-600" />
                </div>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Daily Average</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {Math.round(stats.last7.tickets / 7)} tickets
                  </p>
                  <p className="text-sm text-slate-500">
                    LKR {Math.round(stats.last7.total / 7).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaCalendarDay className="text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Performance */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <FaCalendarAlt className="text-violet-500" />
              Monthly Performance
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">This Month</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {stats.last30.tickets} tickets
                  </p>
                  <p className="text-sm text-slate-500">
                    LKR {stats.last30.total.toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                  <FaTicketAlt className="text-violet-600" />
                </div>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-600 uppercase tracking-wide">Daily Average</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {Math.round(stats.last30.tickets / 30)} tickets
                  </p>
                  <p className="text-sm text-slate-500">
                    LKR {Math.round(stats.last30.total / 30).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FaCalendarDay className="text-amber-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>

      {/* Team Floating Navigation */}
      <TeamFloatingNavigation />
    </div>
  );
}
